"use client";

import React, { createContext, useCallback, useEffect, useRef, useState } from "react";
import {
  DEFAULT_THEME,
  getPreset,
  getFontFamily,
  type ThemeSettings,
  type ThemeColors,
} from "./presets";
import { api } from "@/lib/api";
import {
  boundaryVariant,
  deepPole,
  readableForeground,
  scrimColor,
  readableMuted,
  mutedBackdrops,
  surfaceHover,
  strongVariant,
  strongFor,
} from "./scale";

/**
 * The colour derivations themselves live in ./scale, next to the tint and
 * deepen percentages they have to agree with.
 *
 * They are re-exported here because tests and older call sites import them from
 * this module, and because this is still where they are *applied* to the DOM.
 */
export { boundaryVariant, deepPole, readableForeground, readableMuted, scrimColor, strongVariant, surfaceHover };

/* ── helpers ── */

/** State colours that get an auto-derived `-strong` companion token. */
const STRONG_KEYS = ["danger", "success", "warning", "severe", "info", "primary", "secondary"] as const;

/** لقطة متغيّرات الثيم المطبَّقة لكلّ وضع — يقرؤها سكربت ما قبل الرسم في app/layout.tsx
 * فتُرسم الصفحة بألوان الثيم المحفوظ من أوّل لحظة (كان الداكن يومض بأزرق `.dark` الافتراضيّ
 * في globals.css عند كلّ تحديث، مع كلّ الثيمات — 2026-10-07). المسوّدة لا تُلتقط. */
export const PREPAINT_KEY = "mc-theme-vars:";
function snapshotThemeVars(mode: "light" | "dark") {
  try {
    localStorage.setItem(PREPAINT_KEY + mode, document.documentElement.style.cssText);
  } catch { /* storage full or blocked — the theme still applies, only the flash returns */ }
}

function applyThemeToDOM(settings: ThemeSettings, mode: "light" | "dark") {
  const preset = getPreset(settings.preset, settings.customPresets);
  const base: ThemeColors = mode === "dark" ? preset.dark : preset.light;

  // merge any custom color overrides
  const merged = { ...base, ...settings.colors } as ThemeColors;

  const root = document.documentElement;
  // helpers: convert between hex and "R G B" triplet
  function hexToTriplet(hex: string | null): string | null {
    if (!hex) return null;
    const h = hex.replace('#', '').trim();
    if (!/^[0-9a-fA-F]{6}$/.test(h)) return null;
    const r = parseInt(h.slice(0,2), 16);
    const g = parseInt(h.slice(2,4), 16);
    const b = parseInt(h.slice(4,6), 16);
    return `${r} ${g} ${b}`;
  }
  function tripletToHex(trip: string): string | null {
    const parts = trip.trim().split(/\s+/);
    if (parts.length !== 3) return null;
    const [r,g,b] = parts.map((p) => parseInt(p, 10));
    if ([r,g,b].some((n) => Number.isNaN(n) || n < 0 || n > 255)) return null;
    const toHex = (n: number) => n.toString(16).padStart(2, '0');
    return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
  }

  const resolvedTriplets: Record<string, string> = {};

  for (const [key, rawValue] of Object.entries(merged)) {
    let triplet: string | null = null;
    let hex: string | null = null;
    const v = (rawValue ?? "").toString().trim();
    if (/^#?[0-9a-fA-F]{6}$/.test(v)) {
      // hex provided
      hex = v.startsWith('#') ? v : `#${v}`;
      triplet = hexToTriplet(hex);
    } else if (/^\d+\s+\d+\s+\d+$/.test(v)) {
      // triplet provided
      triplet = v;
      hex = tripletToHex(v);
    } else {
      // fallback: try to parse rgb(...) strings
      const m = v.match(/rgb\((\d+)\s*,?\s*(\d+)\s*,?\s*(\d+)\)/i);
      if (m) {
        triplet = `${m[1]} ${m[2]} ${m[3]}`;
        hex = tripletToHex(triplet);
      }
    }

    if (triplet) {
      root.style.setProperty(`--color-${key}`, triplet);
      resolvedTriplets[key] = triplet;
    }
    if (hex) root.style.setProperty(`--${key}`, hex);

    // Derive a readable foreground for the primary colour so that text placed on
    // `bg-th-primary` keeps sufficient contrast for light presets as well.
    if (key === "primary" && triplet) {
      const fg = readableForeground(triplet);
      root.style.setProperty("--color-primary-foreground", fg.triplet);
      root.style.setProperty("--primary-foreground", fg.hex);
    }
  }

  // Derived companion tokens. These back the semantic colour scales declared in
  // tailwind.config.ts, so a state colour keeps enough contrast in BOTH modes
  // without storing a second colour per preset.
  //
  // `strongVariant` deliberately serves both roles — the fill and the text on a
  // tint of that fill — because it checks the colour against both backdrops. A
  // second, text-only derivation was added here and then removed: it optimised
  // against the surface alone and so scored worse on the tinted card, failing 17
  // of 60 preset/mode/family combinations where `strong` failed none.
  const surfaceTriplet = resolvedTriplets.surface;
  if (surfaceTriplet) {
    for (const key of STRONG_KEYS) {
      const base = resolvedTriplets[key];
      if (!base) continue;
      const strong = strongFor(base, { ...resolvedTriplets, surface: surfaceTriplet });
      root.style.setProperty(`--color-${key}-strong`, strong);
      // Label colour for a SOLID fill of this state colour. It is derived from
      // `strong`, not from `base`, because some preset colours (sky-600 in
      // smart-blue, for example) sit in a dead zone where neither white nor
      // near-black reaches 4.5:1. Solid interactive surfaces therefore use
      // `bg-{key}-700` + `text-{key}-foreground`, which is always AA-safe.
      root.style.setProperty(`--color-${key}-foreground`, readableForeground(strong).triplet);
      // Which way shades 800–950 travel for hover and active states. Publishing
      // the pole instead of the finished colour lets the config interpolate all
      // three depths from one variable.
      root.style.setProperty(`--color-${key}-deep-pole`, deepPole(strong, surfaceTriplet));
    }
  }

  // A border that can carry a boundary rather than just a division. The stored
  // `--color-border` stays exactly as the admin chose it, and dividers keep
  // using it; this is the same colour walked towards the text only as far as
  // 3:1 requires, for the outline of a form control.
  //
  // Walked against every backdrop the class can land on — the page, the card,
  // and the inset — not just the card. Checkboxes on `bg-th-bg` were the case
  // that exposed it: the token cleared 3.00:1 on the surface it was derived
  // against and 2.98:1 on the one it was actually painted on.
  const textTriplet = resolvedTriplets.text;
  if (surfaceTriplet && textTriplet) {
    const borderTriplet = resolvedTriplets.border;
    if (borderTriplet) {
      root.style.setProperty(
        "--color-border-strong",
        boundaryVariant(
          borderTriplet,
          [resolvedTriplets.bg, surfaceTriplet, resolvedTriplets["surface-alt"]].filter(
            Boolean,
          ) as string[],
          textTriplet,
        ),
      );
    }

    // Hover shades for the two page-level surfaces. `bg-th-surface-hover` and
    // `bg-th-bg-hover` were already written in five places and resolved to
    // nothing, so those rows and buttons had no hover state at all.
    //
    // Derived rather than stored, for the same reason as the boundary colour:
    // a hover is not an independent design decision, it is "this surface, a
    // little further from the page". Storing it would put two colours in
    // Settings that must agree, and nothing to make them agree.
    root.style.setProperty(
      "--color-surface-hover",
      surfaceHover(surfaceTriplet, textTriplet),
    );
    if (resolvedTriplets.bg) {
      root.style.setProperty(
        "--color-bg-hover",
        surfaceHover(resolvedTriplets.bg, textTriplet),
      );
    }

    // Secondary text, re-checked against every surface it is actually painted
    // on rather than only the page background.
    //
    // This overwrites the `--color-text-muted` written from the stored palette
    // a few lines above, and is deliberately the last word on that token: the
    // admin still chooses the tone, the theme only refuses to let that tone
    // become illegible. On the palettes that already clear 4.5:1 the value
    // comes back byte-identical, so nothing visibly changes for them.
    const mutedTriplet = resolvedTriplets["text-muted"];
    if (mutedTriplet) {
      root.style.setProperty(
        "--color-text-muted",
        readableMuted(mutedTriplet, mutedBackdrops({ ...resolvedTriplets, surface: surfaceTriplet }), textTriplet),
      );
    }
  }

  // Modal / video-wall scrim. Derived from the page background so it keeps the
  // theme's hue, and always darker than it so opening a modal dims the page in
  // BOTH schemes. Consumers use `bg-th-overlay/40`, `/80`, etc.
  const bgTriplet = resolvedTriplets.bg ?? resolvedTriplets.surface;
  if (bgTriplet) {
    root.style.setProperty("--color-overlay", scrimColor(bgTriplet));
  }

  // fonts
  root.style.setProperty("--font-heading", getFontFamily(settings.fonts.heading));
  root.style.setProperty("--font-body", getFontFamily(settings.fonts.body));

  // layout tokens
  root.style.setProperty("--card-radius", radiusValue(settings.layout.cardRadius));
  root.style.setProperty("--density", densityValue(settings.layout.density));
  root.style.setProperty("--btn-radius", buttonRadiusValue(settings.ui?.buttonStyle || "rounded"));
  root.style.setProperty("--btn-py", buttonPyValue(settings.ui?.buttonSize || "md"));
  root.style.setProperty("--btn-px", buttonPxValue(settings.ui?.buttonSize || "md"));
  root.style.setProperty("--btn-fs", buttonFontSizeValue(settings.ui?.buttonSize || "md"));
  root.style.setProperty("--icon-size", iconSizeValue(settings.ui?.iconSize || "md"));
  root.style.setProperty("--icon-radius", iconRadiusValue(settings.ui?.iconStyle || "rounded"));

  // custom CSS
  let styleEl = document.getElementById("mc-custom-css");
  if (!styleEl) {
    styleEl = document.createElement("style");
    styleEl.id = "mc-custom-css";
    document.head.appendChild(styleEl);
  }
  styleEl.textContent = settings.custom_css || "";

  // global UI identity styles for shared Button component and icon wrappers
  let uiStyleEl = document.getElementById("mc-theme-ui");
  if (!uiStyleEl) {
    uiStyleEl = document.createElement("style");
    uiStyleEl.id = "mc-theme-ui";
    document.head.appendChild(uiStyleEl);
  }
  uiStyleEl.textContent = `
    button[data-variant], a[data-variant] {
      border-radius: var(--btn-radius) !important;
      font-size: var(--btn-fs) !important;
      padding-inline: var(--btn-px) !important;
      padding-block: var(--btn-py) !important;
    }
    button[data-variant][data-size="sm"], a[data-variant][data-size="sm"],
    button[data-variant][data-size="md"], a[data-variant][data-size="md"],
    button[data-variant][data-size="lg"], a[data-variant][data-size="lg"] {
      height: auto !important;
      min-height: calc(var(--btn-py) * 2 + 1.5em) !important;
    }
    button[data-variant][data-size="icon"], a[data-variant][data-size="icon"] {
      width: var(--icon-size) !important;
      height: var(--icon-size) !important;
      border-radius: var(--icon-radius) !important;
      padding: 0 !important;
    }
    .icon-surface {
      width: var(--icon-size);
      height: var(--icon-size);
      border-radius: var(--icon-radius);
      display: inline-flex;
      align-items: center;
      justify-content: center;
    }
  `;
}

function radiusValue(r: string): string {
  switch (r) {
    case "none": return "0";
    case "sm": return "0.375rem";
    case "md": return "0.5rem";
    case "lg": return "0.75rem";
    case "xl": return "1rem";
    case "2xl": return "1.5rem";
    default: return "1rem";
  }
}

function densityValue(d: string): string {
  switch (d) {
    case "compact": return "0.25rem";
    case "normal": return "0.5rem";
    case "comfortable": return "0.75rem";
    default: return "0.5rem";
  }
}

function buttonRadiusValue(style: string): string {
  switch (style) {
    case "pill":
      return "9999px";
    case "square":
      return "0.25rem";
    case "rounded":
    default:
      return "0.65rem";
  }
}

function buttonPyValue(size: string): string {
  switch (size) {
    case "sm":
      return "0.35rem";
    case "lg":
      return "0.7rem";
    case "md":
    default:
      return "0.5rem";
  }
}

function buttonPxValue(size: string): string {
  switch (size) {
    case "sm":
      return "0.75rem";
    case "lg":
      return "1.25rem";
    case "md":
    default:
      return "1rem";
  }
}

function buttonFontSizeValue(size: string): string {
  switch (size) {
    case "sm":
      return "0.8rem";
    case "lg":
      return "1rem";
    case "md":
    default:
      return "0.9rem";
  }
}

function iconSizeValue(size: string): string {
  switch (size) {
    case "sm":
      return "1.75rem";
    case "lg":
      return "2.5rem";
    case "md":
    default:
      return "2.125rem";
  }
}

function iconRadiusValue(style: string): string {
  switch (style) {
    case "square":
      return "0.35rem";
    case "rounded":
    default:
      return "9999px";
  }
}

const LS_KEY = "mc-theme-cache";

function loadCachedTheme(): ThemeSettings {
  if (typeof window === "undefined") return DEFAULT_THEME;
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (raw) return JSON.parse(raw);
  } catch { /* ignore */ }
  return DEFAULT_THEME;
}

function cacheTheme(s: ThemeSettings) {
  try {
    localStorage.setItem(LS_KEY, JSON.stringify(s));
  } catch { /* ignore */ }
}

/* ── Context ── */
interface ThemeContextValue {
  settings: ThemeSettings;
  setSettings: (s: ThemeSettings) => void;
  applyPreview: (s: ThemeSettings) => void;
  saving: boolean;
  save: (s: ThemeSettings) => Promise<void>;
  loading: boolean;
}

export const ThemeContext = createContext<ThemeContextValue>({
  settings: DEFAULT_THEME,
  setSettings: () => {},
  applyPreview: () => {},
  saving: false,
  save: async () => {},
  loading: true,
});

/* ── Provider ── */
export function McThemeProvider({ children }: { children: React.ReactNode }) {
  const [settings, setSettingsState] = useState<ThemeSettings>(loadCachedTheme);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  // detect current color mode
  const getMode = useCallback((): "light" | "dark" => {
    if (typeof document === "undefined") return "light";
    return document.documentElement.classList.contains("dark") ? "dark" : "light";
  }, []);

  /* What the page is SHOWING, which is not always what is saved: the Settings
     page previews an unsaved draft through applyPreview. A light/dark switch
     must re-apply THIS, not `settings` -- re-applying the saved theme dropped
     the draft from the page while "save changes (1)" was still offered, so
     the preview showed something other than what would be saved. */
  const shownRef = useRef<ThemeSettings>(settings);
  const savedRef = useRef<ThemeSettings>(settings);

  // apply to DOM
  const applyPreview = useCallback(
    (s: ThemeSettings) => {
      shownRef.current = s;
      applyThemeToDOM(s, getMode());
    },
    [getMode]
  );

  // set + apply + cache
  const setSettings = useCallback(
    (s: ThemeSettings) => {
      setSettingsState(s);
      cacheTheme(s);
      savedRef.current = s;
      applyPreview(s);
      snapshotThemeVars(getMode());
    },
    [applyPreview, getMode]
  );

  // save to backend
  const save = useCallback(
    async (s: ThemeSettings) => {
      setSaving(true);
      try {
        await api.put("/admin/theme", s);
        setSettings(s);
      } finally {
        setSaving(false);
      }
    },
    [setSettings]
  );

  // fetch from backend on mount
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get<ThemeSettings>("/admin/theme");
        if (!cancelled) setSettings(res.data);
      } catch {
        // use cached/default — no problem
        applyPreview(settings);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // re-apply when dark/light toggles -- whatever is on screen, draft included
  useEffect(() => {
    const observer = new MutationObserver(() => {
      applyThemeToDOM(shownRef.current, getMode());
      // the saved theme in the other mode (not a draft) — its snapshot for the next load
      if (shownRef.current === savedRef.current) snapshotThemeVars(getMode());
    });
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["class"],
    });
    return () => observer.disconnect();
  }, [getMode]);

  return (
    <ThemeContext.Provider value={{ settings, setSettings, applyPreview, saving, save, loading }}>
      {children}
    </ThemeContext.Provider>
  );
}
